import React from "react";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchAllCards, fetchCardsByType, fetchAllCardTypes, deleteCard } from "@/features/cards/api/cardsApi";
import { Card, CardType } from "@/features/cards/types";
import { createCardColumns } from "@/features/cards/components/cardColumns";
import AddCardDialog from "@/features/cards/components/AddCardDialog";
import EditCardDialog from "@/features/cards/components/EditCardDialog";

export default function CardsPage() {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingCard, setEditingCard] = React.useState<Card | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [selectedTypeId, setSelectedTypeId] = React.useState<string>("all");
  const confirmActionRef = React.useRef<() => void>(() => {});

  const { data: cardTypes = [] } = useQuery<CardType[]>({
    queryKey: ["card-types"],
    queryFn: fetchAllCardTypes,
    staleTime: 1000 * 60 * 5,
  });

  const cardsQueryKey = ["cards", selectedTypeId];
  const { data: cards = [], isLoading } = useQuery<Card[]>({
    queryKey: cardsQueryKey,
    queryFn: () =>
      selectedTypeId === "all"
        ? fetchAllCards()
        : fetchCardsByType(Number(selectedTypeId)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCard(id),
    onMutate: () => toast.loading("Deleting card..."),
    onSuccess: () => {
      toast.dismiss();
      toast.success("Card deleted");
      queryClient.invalidateQueries({ queryKey: ["cards"] });
      setConfirmOpen(false);
    },
    onError: () => {
      toast.dismiss();
      toast.error("Failed to delete card");
      setConfirmOpen(false);
    },
  });

  const handleEdit = React.useCallback((card: Card) => {
    setEditingCard(card);
    setEditOpen(true);
  }, []);

  const handleDelete = React.useCallback(
    (card: Card) => {
      confirmActionRef.current = () => {
        setConfirmOpen(false);
        deleteMutation.mutate(card.id);
      };
      setConfirmOpen(true);
    },
    [deleteMutation]
  );

  const columns = React.useMemo(
    () => createCardColumns({ onEdit: handleEdit, onDelete: handleDelete, cardTypes }),
    [handleEdit, handleDelete, cardTypes]
  );

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">Cards</h1>
      <p className="text-muted-foreground">Manage all cards across different types.</p>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Filter by type:</span>
          <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {cardTypes.map((ct) => (
                <SelectItem key={ct.id} value={String(ct.id)}>
                  {ct.type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button size="sm" onClick={() => setAddOpen(true)} className="flex items-center gap-2">
          <Plus size={16} /> Add Card
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="animate-spin w-6 h-6" />
        </div>
      ) : (
        <DataTable columns={columns} data={cards} placeholder="Filter cards" filterColumns={["title"]} />
      )}

      <AddCardDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        cardTypes={cardTypes}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ["cards"] })}
      />

      <EditCardDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        card={editingCard}
        cardTypes={cardTypes}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["cards"] })}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        actionButtonRef={confirmActionRef}
        isLoading={deleteMutation.isPending}
        AlertdialogDescription="Are you sure you want to delete this card? This action cannot be undone."
      />
    </div>
  );
}
